import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useNavigate, useParams } from "react-router-dom";
import io from "socket.io-client";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";
import {
  Typography,
  Button,
  Space,
  Tag,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
dayjs.locale("es");

const normalizeReservationDateKey = (value = "") => String(value).slice(0, 10);

const getReservationGroupKey = (reservation) => {
  if (reservation.comboGroupId) {
    return `combo-${reservation.comboGroupId}`;
  }

  return `booking-${reservation.id}`;
};

export default function CalendarReservation() {
  const navigate = useNavigate();
  const { date: dateParam } = useParams();
  const parsedViewDay = dateParam ? dayjs(dateParam, "YYYY-MM-DD", true) : null;
  const viewDay = parsedViewDay?.isValid() ? parsedViewDay : null;

  const [reservations, setReservations] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => viewDay || dayjs());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedReservationKey, setSelectedReservationKey] = useState(null);

  const fetchReservations = useCallback(() => {
    fetch(`${API_BASE}/booking`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setReservations(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, []);

  /* =======================
     FETCH RESERVAS
     ======================= */
  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleAvailabilityUpdate = () => {
      fetchReservations();
    };

    socket.on("availability:updated", handleAvailabilityUpdate);

    return () => {
      socket.off("availability:updated", handleAvailabilityUpdate);
      socket.disconnect();
    };
  }, [fetchReservations]);

  /* =======================
     AGRUPAR POR FECHA
     ======================= */
  const reservationsByDate = useMemo(() => {
    const map = {};
    reservations.forEach(r => {
      const key = normalizeReservationDateKey(r.date);
      if (!key) {
        return;
      }
      map[key] = [...(map[key] || []), r];
    });
    return map;
  }, [reservations]);

  const getDayReservations = useCallback(
    (date) => reservationsByDate[date.format("YYYY-MM-DD")] || [],
    [reservationsByDate]
  );

  /* =======================
     RESERVAS DEL DÍA
     ======================= */
  const dayReservations = useMemo(() => {
    if (!viewDay) return [];
    return getDayReservations(viewDay);
  }, [viewDay, getDayReservations]);

  const dayReservationGroups = useMemo(() => {
    const grouped = new Map();

    dayReservations.forEach((reservation) => {
      const key = getReservationGroupKey(reservation);
      const currentGroup = grouped.get(key);

      if (currentGroup) {
        currentGroup.items.push(reservation);
        return;
      }

      grouped.set(key, {
        key,
        date: reservation.date,
        time: reservation.time,
        user: reservation.user,
        combo: Boolean(
          reservation.comboGroupId || (reservation.comboServiceIds?.length || 0) > 1
        ),
        items: [reservation],
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      if (a.time === b.time) {
        return (a.user?.name || "").localeCompare(b.user?.name || "");
      }

      return a.time.localeCompare(b.time);
    });
  }, [dayReservations]);

  const employeeColumns = useMemo(() => {
    const groupedByEmployee = new Map();

    dayReservationGroups.forEach((group) => {
      group.items.forEach((reservation) => {
        const employeeId = reservation.employee?.id;
        if (!employeeId) {
          return;
        }

        const currentEmployee = groupedByEmployee.get(employeeId) || {
          employee: reservation.employee,
          reservations: [],
        };

        currentEmployee.reservations.push({
          ...group,
          employeeReservation: reservation,
        });
        groupedByEmployee.set(employeeId, currentEmployee);
      });
    });

    return Array.from(groupedByEmployee.values())
      .map((entry) => ({
        ...entry,
        reservations: entry.reservations.sort((a, b) => {
          if (a.time === b.time) {
            return (a.user?.name || "").localeCompare(b.user?.name || "");
          }

          return a.time.localeCompare(b.time);
        }),
      }))
      .sort((a, b) =>
        (a.employee?.name || "").localeCompare(b.employee?.name || "")
      );
  }, [dayReservationGroups]);

  const selectEmployee = (employee) => {
    if (!viewDay || !employee?.id) {
      return;
    }
    const employeeId = String(employee.id);

    if (selectedEmployeeId !== employeeId) {
      setSelectedEmployeeId(employeeId);
      setSelectedReservationKey(null);
      return;
    }

    navigate(
      `/admin/reservas?date=${viewDay.format("YYYY-MM-DD")}&employeeId=${employee?.id}&employeeName=${encodeURIComponent(
        [employee?.name, employee?.lastName].filter(Boolean).join(" ")
      )}`
    );
  };

  const selectReservation = ({ employee, group }) => {
    if (!viewDay || !employee?.id || !group?.time) {
      return;
    }

    const reservationKey = `${employee.id}-${group.key}`;

    if (selectedReservationKey !== reservationKey) {
      setSelectedEmployeeId(null);
      setSelectedReservationKey(reservationKey);
      return;
    }

    navigate(
      `/admin/reservas?date=${viewDay.format("YYYY-MM-DD")}&time=${group.time}&employeeId=${employee?.id}&employeeName=${encodeURIComponent(
        [employee?.name, employee?.lastName].filter(Boolean).join(" ")
      )}`
    );
  };

  /* =======================
     CALENDARIO MENSUAL
     ======================= */
  const startOfMonth = currentMonth.startOf("month");
  const endOfMonth = currentMonth.endOf("month");
  const startDay = startOfMonth.subtract((startOfMonth.day() + 6) % 7, "day");
  const endDay = endOfMonth.add((7 - ((endOfMonth.day() + 6) % 7) - 1) % 7, "day");

  const days = [];
  let d = startDay;
  while (d.isBefore(endDay) || d.isSame(endDay, "day")) {
    days.push(d);
    d = d.add(1, "day");
  }

  /* =======================
     VISTA DIARIA
     ======================= */
 if (viewDay) {
  return (
    <div style={styles.container}>
      <div style={styles.dailyHeader}>
        <div style={styles.dailyHeaderActions}>
          <Button
            shape="circle"
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin/agenda")}
            aria-label="Volver al calendario"
          />
        </div>
        <div>
          <div style={styles.dailyEyebrow}>Agenda diaria</div>
          <Typography.Title level={2} style={styles.dailyTitle}>
            {viewDay.format("dddd D [de] MMMM [de] YYYY")}
          </Typography.Title>
          <Typography.Text type="secondary">
            Consulta los turnos organizados por profesional.
          </Typography.Text>
        </div>
      </div>

      <div style={styles.daySummary}>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, background: "#e6f4ff", color: "#1677ff" }}>
            <CalendarOutlined />
          </div>
          <div>
            <div style={styles.summaryValue}>{dayReservationGroups.length}</div>
            <div style={styles.summaryLabel}>Reservaciones del día</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, background: "#f6ffed", color: "#52c41a" }}>
            <TeamOutlined />
          </div>
          <div>
            <div style={styles.summaryValue}>{employeeColumns.length}</div>
            <div style={styles.summaryLabel}>Profesionales con turnos</div>
          </div>
        </div>
      </div>

      <div style={styles.employeeGrid}>
        {employeeColumns.length === 0 ? (
          <div style={styles.emptyState}>
            No hay reservas para esta fecha.
          </div>
        ) : (
          employeeColumns.map(({ employee, reservations }) => (
            <div
              key={employee?.id}
              style={{
                ...styles.employeeColumn,
                border:
                  selectedEmployeeId === String(employee?.id)
                    ? "2px solid #ef4444"
                    : styles.employeeColumn.border,
                padding: selectedEmployeeId === String(employee?.id) ? 17 : 18,
                boxShadow:
                  selectedEmployeeId === String(employee?.id)
                    ? "0 0 0 3px rgba(239, 68, 68, 0.10), 0 12px 32px rgba(15,23,42,0.08)"
                    : styles.employeeColumn.boxShadow,
              }}
            >
              <div
                style={{
                  ...styles.employeeHeader,
                  cursor: "pointer",
                }}
                role="button"
                tabIndex={0}
                title={`Ver todas las reservas de ${employee?.name || "este profesional"}`}
                onClick={() => selectEmployee(employee)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectEmployee(employee);
                  }
                }}
              >
                <div
                  style={{
                    ...styles.employeeAvatar,
                    background: employee?.background_color || "#999",
                  }}
                >
                  {employee?.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <div style={styles.employeeName}>{employee?.name}</div>
                  <div style={styles.employeeMeta}>
                    {reservations.length} {reservations.length === 1 ? "turno" : "turnos"}
                  </div>
                </div>
              </div>

              <div style={styles.employeeReservations}>
                {reservations.map((group) => {
                  const duration =
                    group.employeeReservation?.duration ||
                    group.employeeReservation?.service?.duration ||
                    60;
                  const endTime = dayjs(group.time, "HH:mm")
                    .add(duration, "minute")
                    .format("HH:mm");
                  const comboLines = group.items.map((item) => ({
                    id: item.id,
                    serviceName: item.service?.name || "Servicio",
                    employeeName: item.employee?.name || "Sin empleado",
                    isCurrentEmployee:
                      item.employee?.id === group.employeeReservation?.employee?.id,
                  }));

                  return (
                    <div
                      key={`${employee?.id}-${group.key}`}
                      onClick={() => selectReservation({ employee, group })}
                      style={{
                        ...styles.reservationCard,
                        border:
                          selectedReservationKey === `${employee?.id}-${group.key}`
                            ? "2px solid #ef4444"
                            : styles.reservationCard.border,
                        borderLeftColor:
                          group.employeeReservation?.employee?.background_color || "#ccc",
                        boxShadow:
                          selectedReservationKey === `${employee?.id}-${group.key}`
                            ? "0 0 0 3px rgba(239, 68, 68, 0.10), 0 8px 20px rgba(15,23,42,0.08)"
                            : styles.reservationCard.boxShadow,
                      }}
                    >
                      <div style={styles.reservationCardTop}>
                        <span style={styles.reservationTime}>
                          <ClockCircleOutlined /> {group.time} - {endTime}
                        </span>
                        {group.combo ? (
                          <Tag color="purple" style={styles.comboTag}>
                            Combinada
                          </Tag>
                        ) : null}
                      </div>

                      <div style={styles.reservationClient}>
                        {group.user?.name} {group.user?.lastName || ""}
                      </div>

                      {group.combo ? (
                        <div style={styles.comboDetails}>
                          <div style={styles.comboCurrentLabel}>Este empleado realiza</div>
                          <div style={styles.comboCurrentService}>
                            {group.employeeReservation?.service?.name || "Servicio"}
                          </div>

                          <div style={styles.comboTeamLabel}>Reserva completa</div>
                          <div style={styles.comboTeamList}>
                            {comboLines.map((line) => (
                              <div
                                key={line.id}
                                style={{
                                  ...styles.comboTeamItem,
                                  background: line.isCurrentEmployee ? "#f3f0ff" : "#f7f7f7",
                                  borderColor: line.isCurrentEmployee ? "#b197fc" : "#ececec",
                                }}
                              >
                                <span style={styles.comboServiceName}>{line.serviceName}</span>
                                <span style={styles.comboEmployeeName}>{line.employeeName}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div style={styles.reservationService}>
                          {group.employeeReservation?.service?.name}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

  /* =======================
     VISTA MENSUAL
     ======================= */
  return (
    <div style={styles.container}>
      <div style={styles.monthHeader}>
        <Space align="center" size="middle">
          <Button
            icon={<LeftOutlined />}
            onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
          />

          <Typography.Title level={4} style={{ margin: 0 }}>
            {currentMonth.format("MMMM YYYY")}
          </Typography.Title>

          <Button
            icon={<RightOutlined />}
            onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
          />
        </Space>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() =>
            navigate(
              selectedDay
                ? `/admin/nueva-reservacion?date=${selectedDay.format("YYYY-MM-DD")}`
                : "/admin/nueva-reservacion"
            )
          }
        >
          Nueva reservación
        </Button>
      </div>


      <div style={styles.weekDays}>
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((dayName) => (
          <div key={dayName} style={styles.weekDay}>{dayName}</div>
        ))}
      </div>

      <div style={styles.grid}>
        {days.map(d => {
          const dayRes = getDayReservations(d);
          const isSelected = selectedDay && d.isSame(selectedDay, "day");
          return (
            <div
              key={d.format("YYYY-MM-DD")}
              style={{
                ...styles.dayCell,
                border: isSelected ? "2px solid red" : "1px solid #eee",
                background:
                  d.month() !== currentMonth.month() ? "#fafafa" : "#fff"
              }}
              onClick={() => {
                if (isSelected) {
                  navigate(`/admin/agenda/${d.format("YYYY-MM-DD")}`);
                  return;
                }

                setSelectedDay(d);
              }}
              title={isSelected
                ? "Haz clic nuevamente para ver las reservaciones"
                : "Haz clic para seleccionar esta fecha"}
            >
              <div
                style={{
                  ...styles.dayNumber,
                  background: isSelected ? "red" : "transparent",
                  color: isSelected ? "#fff" : "#000"
                }}
              >
                {d.date()}
              </div>

              {dayRes.slice(0, 3).map(r => (
                <div
                  key={r.id}
                  style={{
                    ...styles.badge,
                    background:
                      r.employee?.background_color || "#ccc"
                  }}
                >
                  {r.user?.name}
                </div>
              ))}

              {dayRes.length > 3 && (
                <div style={styles.more}>
                  +{dayRes.length - 3}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =======================
   ESTILOS
   ======================= */

const styles = {
  container: { maxWidth: 1280, margin: "auto", fontFamily: "system-ui", paddingBottom: 24 },
  header: { display: "flex", justifyContent: "space-between", marginBottom: 16 },
  monthHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  weekDays: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", fontWeight: "bold" },
  weekDay: { padding: 8 },
  grid: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 },
  dayCell: { minHeight: 110, padding: 6, borderRadius: 8, cursor: "pointer" },
  dayNumber: { width: 28, height: 28, borderRadius: "50%", textAlign: "center", lineHeight: "28px", fontWeight: "bold", marginBottom: 4 },
  badge: { fontSize: 12, padding: "2px 6px", borderRadius: 6, marginBottom: 2, color: "#fff", },
  more: { fontSize: 12, color: "#666" },
  backButton: { marginBottom: 12 },
  dailyHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 18,
    padding: "8px 0 28px",
  },
  dailyHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  dailyEyebrow: {
    color: "#1677ff",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    marginBottom: 4,
  },
  dailyTitle: {
    margin: 0,
    textTransform: "capitalize",
    letterSpacing: -0.5,
  },
  daySummary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginBottom: 28,
  },
  summaryCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: "#fff",
    border: "1px solid #edf0f3",
    borderRadius: 16,
    padding: "18px 20px",
    minWidth: 220,
    boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
  },
  summaryIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    fontSize: 20,
    flexShrink: 0,
  },
  summaryValue: { fontSize: 26, fontWeight: 750, lineHeight: 1.05, color: "#172321" },
  summaryLabel: { fontSize: 12, color: "#667085", marginTop: 4 },
  employeeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 20,
    alignItems: "start",
  },
  employeeColumn: {
    background: "#fff",
    border: "1px solid #edf0f3",
    borderRadius: 18,
    padding: 18,
    boxShadow: "0 14px 40px rgba(15,23,42,0.06)",
  },
  employeeHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: "1px solid #f0f0f0",
  },
  employeeAvatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontSize: 15,
    fontWeight: 750,
  },
  employeeName: { fontWeight: 750, fontSize: 16, color: "#172321" },
  employeeMeta: { fontSize: 12, color: "#667085", marginTop: 2 },
  employeeReservations: { display: "flex", flexDirection: "column", gap: 12 },
  reservationCard: {
    border: "1px solid #edf0f3",
    borderLeft: "4px solid #ccc",
    borderRadius: 14,
    padding: 14,
    cursor: "pointer",
    background: "#fff",
    boxShadow: "0 5px 16px rgba(15,23,42,0.04)",
  },
  reservationCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  reservationTime: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#475467", background: "#f2f4f7", padding: "5px 8px", borderRadius: 8 },
  comboTag: { marginInlineEnd: 0 },
  reservationClient: { fontSize: 15, fontWeight: 750, margin: "10px 0 4px", color: "#172321" },
  reservationService: { fontSize: 13, color: "#667085", lineHeight: 1.45 },
  comboDetails: { display: "flex", flexDirection: "column", gap: 8 },
  comboCurrentLabel: {
    fontSize: 11,
    color: "#777",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  comboCurrentService: {
    fontSize: 14,
    fontWeight: 700,
    color: "#2f2f2f",
  },
  comboTeamLabel: {
    fontSize: 11,
    color: "#777",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  comboTeamList: { display: "flex", flexDirection: "column", gap: 6 },
  comboTeamItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    border: "1px solid #ececec",
    borderRadius: 8,
    padding: "6px 8px",
  },
  comboServiceName: { fontSize: 12, fontWeight: 600, color: "#444" },
  comboEmployeeName: { fontSize: 12, color: "#666", textAlign: "right" },
  emptyState: {
    border: "1px dashed #d9d9d9",
    borderRadius: 12,
    padding: 36,
    color: "#777",
    textAlign: "center",
    background: "#fff",
    gridColumn: "1 / -1",
  },
};
 
